import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ScanLine, ArrowRight, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success("Se l'email esiste, riceverai un link a breve.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore invio email");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:block relative">
        <img
          src="https://images.pexels.com/photos/4258830/pexels-photo-4258830.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="paper documents"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0A0A0A]/25 mix-blend-multiply" />
        <div className="relative h-full flex flex-col justify-between p-10 text-white">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white flex items-center justify-center rounded-xl">
              <ScanLine className="w-4 h-4 text-[#0A0A0A]" />
            </div>
            <span className="font-display font-bold text-lg">Archivio Smart</span>
          </div>
          <div>
            <div className="overline text-white/70 mb-3">— RECUPERO ACCESSO</div>
            <p className="font-display text-3xl leading-tight max-w-sm">
              Ti rimandiamo dentro al tuo archivio in un attimo.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 bg-[#F8F8F6]">
        <div className="w-full max-w-sm">
          <Link
            to="/login"
            data-testid="forgot-back-to-login"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#5C5C5C] hover:text-[#0A0A0A] mb-6"
          >
            <ArrowLeft className="w-3 h-3" /> Torna al login
          </Link>
          <div className="overline text-[#0A0A0A] mb-3">— PASSWORD DIMENTICATA</div>
          <h1 className="font-display font-bold text-4xl tracking-tighter mb-2">Reimposta password.</h1>
          <p className="text-sm text-[#5C5C5C] mb-10">
            Inserisci l'email del tuo account. Ti mandiamo un link per crearne una nuova.
          </p>

          {sent ? (
            <div
              data-testid="forgot-sent-confirmation"
              className="rounded-xl border border-black/10 bg-white p-5 text-sm text-[#0A0A0A]"
            >
              <p className="font-medium mb-1">Controlla la tua casella email.</p>
              <p className="text-[#5C5C5C]">
                Se esiste un account con <span className="font-medium">{email}</span>, riceverai un link valido 60 minuti.
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-5">
              <div>
                <Label htmlFor="email" className="text-xs uppercase tracking-widest">Email</Label>
                <Input
                  data-testid="forgot-email-input"
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="rounded-xl mt-2 bg-white h-11 focus-visible:ring-[#0A0A0A]"
                  placeholder="tuo@indirizzo.it"
                />
              </div>
              <Button
                data-testid="forgot-submit-btn"
                disabled={loading}
                type="submit"
                className="w-full rounded-xl bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white h-11"
              >
                {loading ? "Invio…" : (<>Invia link <ArrowRight className="ml-2 w-4 h-4" /></>)}
              </Button>
            </form>
          )}

          <div className="mt-8 text-sm text-[#5C5C5C]">
            Ricordi la password?{" "}
            <Link data-testid="forgot-goto-login" to="/login" className="text-[#0A0A0A] hover:underline">
              Accedi
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
